import { read as readFootprint, intersectionArea } from '../../utils/applicationFootprint';

const MIN_COVERAGE_RATIO = 0.95;
const MAX_COVERAGE_RATIO = 1.05;
const MAX_POLYGON_AREA_DIFFERENCE_RATIO = 0.03;
const MAX_GROUP_START_GAP_MS = 8 * 24 * 60 * 60 * 1000;
const MAX_OVERLAP_RATIO = 0.10;
const WEB_MERCATOR_RADIUS = 6378137;

const numberOrNull = value => value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
  ? null
  : Number(value);

const areasWithinTolerance = (left, right) => {
  if (left === null || right === null) return left === right;
  const largerArea = Math.max(Math.abs(left), Math.abs(right));
  if (largerArea === 0) return true;
  const differenceRatio = Math.abs(left - right) / largerArea;
  return differenceRatio <= MAX_POLYGON_AREA_DIFFERENCE_RATIO + Number.EPSILON * 4;
};

const dateValue = value => {
  const timestamp = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
};

const applicationId = (application, index) => application.id_job_area_link
  ? `link:${application.id_job_area_link}`
  : `application:${application.id_job}:${application.execution_guid}:${application.start_date}:${index}`;

const footprintCells = value => {
  return readFootprint(value);
};

const projectedPolygonRings = value => {
  try {
    const points = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(points) || !points.length) return null;
    const rings = Array.isArray(points[0][0]) ? points : [points];
    const projected = rings.map(ring => ring.map(point => {
      const latitude = Math.max(-85.05112878, Math.min(85.05112878, Number(point[0])));
      const longitude = Number(point[1]);
      return [WEB_MERCATOR_RADIUS * longitude * Math.PI / 180,
        WEB_MERCATOR_RADIUS * Math.log(Math.tan(Math.PI / 4 + latitude * Math.PI / 360))];
    }));
    return projected.every(ring => ring.length >= 3 && ring.every(point => point.every(Number.isFinite)))
      ? projected : null;
  } catch (error) {
    return null;
  }
};

const overlapRatio = (a, b, polygonRings) => {
  const left = a._footprint;
  const right = b._footprint;
  const leftAreaHa = numberOrNull(a.area_app_ha);
  const rightAreaHa = numberOrNull(b.area_app_ha);
  if (!left || !right || !(leftAreaHa > 0) || !(rightAreaHa > 0)) return null;

  return intersectionArea(left, right, polygonRings) / ((leftAreaHa + rightAreaHa) * 10000);
};

const canJoinComplementaryGroup = (group, candidate, polygonRings) => {
  const candidateTime = dateValue(candidate.start_date);
  if (candidateTime === null || !candidate._footprint) return false;

  const groupStart = Math.min(...group.map(application => dateValue(application.start_date)));
  if (!Number.isFinite(groupStart) || candidateTime - groupStart > MAX_GROUP_START_GAP_MS) return false;

  return group.every(application => {
    const ratio = overlapRatio(application, candidate, polygonRings);
    return ratio !== null && ratio <= MAX_OVERLAP_RATIO + Number.EPSILON * 4;
  });
};

const findComplementaryGroups = (applications, polygonRings) => {
  const pending = applications.slice().sort((a, b) => (dateValue(a.start_date) || 0) - (dateValue(b.start_date) || 0));
  const groups = [];

  while (pending.length > 0) {
    const first = pending.shift();
    const group = [first];
    if (dateValue(first.start_date) !== null && first._footprint) {
      for (let index = 0; index < pending.length;) {
        const candidate = pending[index];
        const candidateTime = dateValue(candidate.start_date);
        if (candidateTime !== null && candidateTime - dateValue(first.start_date) > MAX_GROUP_START_GAP_MS) break;
        if (canJoinComplementaryGroup(group, candidate, polygonRings)) {
          group.push(candidate);
          pending.splice(index, 1);
        } else {
          index += 1;
        }
      }
    }
    groups.push(group);
  }

  return groups;
};

const summarizeAreaGroup = (area, applications, status, groupIndex) => {
  const appliedValues = applications.map(item => numberOrNull(item.area_app_ha)).filter(value => value !== null);
  const volumeValues = applications.map(item => numberOrNull(item.volume_l)).filter(value => value !== null);
  const starts = applications.map(item => ({ value: item.start_date, time: dateValue(item.start_date) }))
    .filter(item => item.time !== null).sort((a, b) => a.time - b.time);
  const ends = applications.map(item => ({ value: item.end_date, time: dateValue(item.end_date) }))
    .filter(item => item.time !== null).sort((a, b) => b.time - a.time);

  return {
    ...area,
    key: `${area.key}:${status}:${groupIndex}:${applications.map(item => item.id_job_area_link || item.id_job).join(',')}`,
    status,
    applications: applications.slice().sort((a, b) => (dateValue(a.start_date) || 0) - (dateValue(b.start_date) || 0)),
    area_app_ha: appliedValues.length ? appliedValues.reduce((sum, value) => sum + value, 0) : null,
    volume_l: volumeValues.length ? volumeValues.reduce((sum, value) => sum + value, 0) : null,
    start_date: starts.length ? starts[0].value : null,
    end_date: ends.length ? ends[0].value : null,
  };
};

export const groupAppliedAreaRows = rows => {
  const areas = [];
  (rows || []).forEach((row, index) => {
    const areaHa = numberOrNull(row.area_ha);
    const areaKey = row.guid
      ? `guid:${row.guid}:area:${areaHa === null ? 'unknown' : areaHa.toFixed(3)}`
      : `application:${applicationId(row, index)}`;
    let area = row.guid
      ? areas.find(candidate => candidate.guid === row.guid && areasWithinTolerance(candidate.area_ha, areaHa))
      : null;
    if (!area) {
      area = {
        key: areaKey,
        guid: row.guid,
        name: row.name,
        points: row.points,
        area_ha: areaHa,
        applications: [],
      };
      areas.push(area);
    }

    const key = applicationId(row, index);
    if (!area.applications.some(application => application._groupKey === key)) {
      area.applications.push({ ...row, _groupKey: key, _footprint: footprintCells(row.application_cells) });
    }
  });

  const groups = Array.from(areas.values()).map(area => {
    const applications = area.applications.slice().sort((a, b) => (dateValue(a.start_date) || 0) - (dateValue(b.start_date) || 0));
    const polygonAreaHa = numberOrNull(area.area_ha);
    const complete = [];
    let partial = applications;

    if (polygonAreaHa !== null && polygonAreaHa > 0) {
      complete.push(...applications.filter(application => {
        const appliedHa = numberOrNull(application.area_app_ha);
        return appliedHa !== null && appliedHa / polygonAreaHa >= MIN_COVERAGE_RATIO;
      }).map(application => [application]));
      partial = applications.filter(application => {
        const appliedHa = numberOrNull(application.area_app_ha);
        return appliedHa === null || appliedHa / polygonAreaHa < MIN_COVERAGE_RATIO;
      });
    }

    const complementaryGroups = findComplementaryGroups(partial, projectedPolygonRings(area.points));
    const remainingPartial = [];
    complementaryGroups.forEach(group => {
      if (group.length < 2) {
        remainingPartial.push(group[0]);
        return;
      }

      const totalApplied = group.reduce((sum, application) => sum + (numberOrNull(application.area_app_ha) || 0), 0);
      const ratio = polygonAreaHa > 0 ? totalApplied / polygonAreaHa : 0;
      if (ratio >= MIN_COVERAGE_RATIO && ratio <= MAX_COVERAGE_RATIO) {
        complete.push(group);
      } else {
        remainingPartial.push(group);
      }
    });

    const completeGroups = complete.map((group, index) => summarizeAreaGroup(area, group, 'complete', index));
    const otherGroups = remainingPartial.map((group, index) =>
      summarizeAreaGroup(area, Array.isArray(group) ? group : [group], 'other', index));
    return [...completeGroups, ...otherGroups];
  }).reduce((allGroups, areaGroups) => allGroups.concat(areaGroups), []);

  return groups.sort((a, b) => {
    const aEnd = dateValue(a.end_date);
    const bEnd = dateValue(b.end_date);
    if (aEnd === null) return bEnd === null
      ? (a.status === 'complete' ? 0 : 1) - (b.status === 'complete' ? 0 : 1)
      : 1;
    if (bEnd === null) return -1;
    return (bEnd - aEnd) || ((a.status === 'complete' ? 0 : 1) - (b.status === 'complete' ? 0 : 1));
  });
};
